// ============================================================================
// crowd_ai.js — behaviour for EVERY non-player combat unit (both armies), with
// distance-to-player LOD:
//   FULL  (<25m)  full steering + separation + attacks + reactions
//   STEER (25-60) steering + attacks, no separation
//   STAT  (>60m)  cheap advance + throttled retargeting (statistical band)
//
// Targeting is team-aware: allies hunt Magmar; Magmar swarm Benjen and allies.
// All proximity work goes through the spatial hash — never O(n^2). Separation is
// neighbour-capped so dense crowds stay bounded.
// ============================================================================

import {
  ST_IDLE, ST_SEEK, ST_ATTACK, ST_DEAD, ST_BLOCK,
  LOD_FULL, LOD_STEER, LOD_STAT, LOD_FULL_R2, LOD_STEER_R2,
  UNIT_FOOTSOLDIER, UNIT_ARCHER, UNIT_CAPTAIN, UNIT_WARLORD,
  TEAM_PLAYER, TEAM_MAGMAR, MAP_HALF, MORALE_MAX,
  RE_FLINCH,
} from '../core/constants.js';
import {
  dist2, headingTo, angleDelta, fwdX, fwdZ, angleApproach, clamp,
} from '../core/math.js';
import { stepReaction, applyReaction } from '../combat/reactions.js';
import { damagePlayer } from '../combat/player.js';
import { EV } from '../core/events.js';

// per-type attack timing / shape
const ATK = {
  [UNIT_FOOTSOLDIER]: { windup: 0.42, recover: 0.45, phases: 1, ranged: false },
  [UNIT_ARCHER]:      { windup: 0.55, recover: 0.75, phases: 1, ranged: true },
  [UNIT_CAPTAIN]:     { windup: 0.38, recover: 0.34, phases: 3, ranged: false },
  [UNIT_WARLORD]:     { windup: 0.34, recover: 0.30, phases: 4, ranged: false },
};

function moraleFactor(world, team) {
  // >1 = emboldened. Player morale high favours allies; low favours Magmar.
  const m = world.morale / MORALE_MAX; // 0..1
  return team === TEAM_PLAYER ? (0.7 + m * 0.6) : (0.7 + (1 - m) * 0.6);
}

// resolve the current target's position; returns null if the target is gone.
// targetSlot === -2 is a positional march order (marchX/marchZ), never null.
const _tp = { x: 0, z: 0, isPlayer: false, unit: null, d: 0 };
function targetPos(world, u) {
  if (u.targetIsPlayer) {
    const p = world.player;
    if (p.state === 'dead') return null;
    _tp.x = p.x; _tp.z = p.z; _tp.isPlayer = true; _tp.unit = null;
    _tp.d = Math.sqrt(dist2(u.x, u.z, p.x, p.z));
    return _tp;
  }
  if (u.targetSlot === -2) {
    _tp.x = u.marchX; _tp.z = u.marchZ; _tp.isPlayer = false; _tp.unit = null;
    _tp.d = Math.sqrt(dist2(u.x, u.z, u.marchX, u.marchZ));
    return _tp;
  }
  if (u.targetSlot >= 0) {
    const t = world.units.get(u.targetSlot);
    if (t && t._active && t.state !== ST_DEAD && t.team !== u.team) {
      _tp.x = t.x; _tp.z = t.z; _tp.isPlayer = false; _tp.unit = t;
      _tp.d = Math.sqrt(dist2(u.x, u.z, t.x, t.z));
      return _tp;
    }
  }
  return null;
}

// pick the nearest hostile (and, for Magmar, weigh Benjen heavily).
function acquireTarget(world, u) {
  const hostileTeam = u.team === TEAM_PLAYER ? TEAM_MAGMAR : TEAM_PLAYER;
  let bestSlot = -1, bestD = u.aggro * u.aggro;
  world.hash.forEachInRadius(u.x, u.z, u.aggro, (slot) => {
    const t = world.units.get(slot);
    if (!t._active || t.team !== hostileTeam || t.state === ST_DEAD) return;
    const d = dist2(u.x, u.z, t.x, t.z);
    if (d < bestD) { bestD = d; bestSlot = slot; }
  });

  // Magmar strongly prefer the player when he's within an extended aggro range.
  if (u.team === TEAM_MAGMAR && world.player.state !== 'dead') {
    const pd = dist2(u.x, u.z, world.player.x, world.player.z);
    const bias = (u.aggro * 1.4) * (u.aggro * 1.4);
    if (pd < bias && (bestSlot < 0 || pd < bestD * 1.6)) {
      u.targetIsPlayer = true; u.targetSlot = -1; return;
    }
  }
  if (bestSlot >= 0) { u.targetSlot = bestSlot; u.targetIsPlayer = false; return; }

  // ---- no hostile in range: fall back ----
  u.targetIsPlayer = false;
  // Officers & leaders hold their ground (leash to home) instead of marching off
  // and dying — this keeps the Warmaster/Dynast/warlords defensible.
  if (u.type === UNIT_WARLORD) {
    u.targetSlot = -2; u.marchX = u.homeX; u.marchZ = u.homeZ; return;
  }
  // Grunts stream toward the NEAREST enemy-held bastion, spreading the armies
  // across objectives rather than funnelling onto the enemy leader.
  const foe = 1 - u.team;
  let bb = null, bd = Infinity;
  for (let i = 0; i < world.bastions.length; i++) {
    const b = world.bastions[i];
    if (b.team !== foe) continue;
    const d = dist2(u.x, u.z, b.x, b.z);
    if (d < bd) { bd = d; bb = b; }
  }
  if (bb) { u.targetSlot = -2; u.marchX = bb.x; u.marchZ = bb.z; return; }
  // no enemy bastions left → converge on the enemy leader for the finale
  const leader = u.team === TEAM_PLAYER ? world.dynast : world.warmaster;
  if (leader && leader._active && leader.state !== ST_DEAD) { u.targetSlot = leader._slot; }
  else u.targetSlot = -1;
}

// neighbour-capped separation so units don't stack (FULL LOD only).
function separate(world, u, dt) {
  let pushX = 0, pushZ = 0, n = 0;
  const R = 1.25;
  world.hash.forEachInRadius(u.x, u.z, R, (slot) => {
    if (n >= 8) return;
    const o = world.units.get(slot);
    if (o === u || !o._active || o.state === ST_DEAD) return;
    const dx = u.x - o.x, dz = u.z - o.z;
    const d2 = dx * dx + dz * dz;
    if (d2 > R * R || d2 < 1e-5) return;
    const inv = 1 / Math.sqrt(d2);
    pushX += dx * inv; pushZ += dz * inv; n++;
  });
  if (n > 0) {
    const s = 2.2 * dt;
    u.x += pushX * s; u.z += pushZ * s;
  }
}

function steer(world, u, tx, tz, dt, speed) {
  const h = headingTo(u.x, u.z, tx, tz);
  u.heading = angleApproach(u.heading, h, 6 * dt);
  u.x += fwdX(u.heading) * speed * dt;
  u.z += fwdZ(u.heading) * speed * dt;
}

function startEnemyAttack(world, u, tx, tz) {
  u.state = ST_ATTACK; u.stateT = 0; u.swungThisPhase = false; u.atkPhase = 0;
  u.heading = headingTo(u.x, u.z, tx, tz);
}

function dealMelee(world, u, tgt) {
  // must still be roughly in front and in range
  const d = Math.sqrt(dist2(u.x, u.z, tgt.x, tgt.z));
  if (d > u.atkRange + 0.7) return;
  const toT = headingTo(u.x, u.z, tgt.x, tgt.z);
  if (Math.abs(angleDelta(u.heading, toT)) > 1.1) return;
  if (tgt.isPlayer) {
    const gb = u.type === UNIT_WARLORD && u.atkPhase === (u.warlord ? u.warlord.guardBreakPhase : 3);
    damagePlayer(world, u.atkDmg, u.x, u.z, gb);
  } else if (tgt.unit) {
    const o = tgt.unit;
    // inter-unit fights hit softer so the background war grinds instead of
    // vaporising — keeps the field populated and morale stable.
    o.hp -= u.atkDmg * 0.5; o.flash = 1;
    world.spawnSpark(o.x, 1.1, o.z, (world.rng() - 0.5) * 4, 2 + world.rng() * 3, (world.rng() - 0.5) * 4, 0.25, 0.5, 0);
    if (o.hp <= 0) world.killUnit(o, false);
    else if (o.type === UNIT_FOOTSOLDIER || o.type === UNIT_ARCHER) applyReaction(world, o, RE_FLINCH, u.x, u.z, 3, 0);
  }
}

function fireBolt(world, u, tgt) {
  const dx = tgt.x - u.x, dz = tgt.z - u.z;
  const d = Math.hypot(dx, dz) || 1;
  const speed = 22;
  const ty = tgt.isPlayer ? 1.3 : 1.2;
  const vy = (ty - 1.3) / (d / speed) + 0.5 * 0; // roughly flat
  world.spawnProjectile(u.x, 1.35, u.z, (dx / d) * speed, vy, (dz / d) * speed, u.team, u.atkDmg, 2.2);
}

function updateAttackAI(world, u, dt, atkDef) {
  u.stateT += dt;
  const strike = atkDef.windup;
  if (!u.swungThisPhase && u.stateT >= strike) {
    u.swungThisPhase = true;
    const tgt = targetPos(world, u);
    if (tgt) {
      if (atkDef.ranged) fireBolt(world, u, tgt);
      else dealMelee(world, u, tgt);
    }
  }
  if (u.stateT >= strike + atkDef.recover) {
    if (u.atkPhase < atkDef.phases - 1) {
      // chain next phase (captains: 3-hit; warlords: 4-hit pattern)
      u.atkPhase++; u.stateT = 0; u.swungThisPhase = false;
      const tgt = targetPos(world, u);
      if (tgt) u.heading = headingTo(u.x, u.z, tgt.x, tgt.z);
    } else {
      u.state = ST_SEEK; u.stateT = 0; u.atkPhase = 0;
      u.atkTimer = u.atkCd / moraleFactor(world, u.team);
    }
  }
}

function updateUnit(world, u, dt) {
  if (u.flash > 0) u.flash = Math.max(0, u.flash - dt * 6);

  // reaction / ragdoll / death physics takes priority
  if (stepReaction(world, u, dt)) {
    if (u.state === ST_DEAD && u.fade <= 0) { world.units.despawn(u); return; }
    confine(world, u);
    return;
  }

  // LOD band
  const pd2 = dist2(u.x, u.z, world.player.x, world.player.z);
  u.lod = pd2 < LOD_FULL_R2 ? LOD_FULL : pd2 < LOD_STEER_R2 ? LOD_STEER : LOD_STAT;

  // throttled decision making (retarget). STAT band decides even less often.
  u.decideT -= dt;
  if (u.decideT <= 0 || u.targetSlot < 0 && !u.targetIsPlayer) {
    u.decideT = (u.lod === LOD_STAT ? 0.9 : 0.35) + world.rng() * 0.3;
    acquireTarget(world, u);
  }

  const atkDef = ATK[u.type];
  const mf = moraleFactor(world, u.team);
  const speed = u.speed * (0.9 + 0.2 * mf);

  if (u.state === ST_ATTACK) {
    updateAttackAI(world, u, dt, atkDef);
    confine(world, u);
    return;
  }

  const tgt = targetPos(world, u);
  if (u.atkTimer > 0) u.atkTimer -= dt;
  if (u.blockCd > 0) u.blockCd -= dt;

  if (!tgt) {
    // idle drift near home
    u.state = ST_IDLE;
    if (u.lod !== LOD_STAT && world.rng() < 0.01) u.heading += (world.rng() - 0.5);
    if (u.lod === LOD_FULL) separate(world, u, dt);
    confine(world, u);
    return;
  }

  const attackable = tgt.isPlayer || tgt.unit != null;
  if (!attackable) {
    // marching to an objective/home position — advance, idle on arrival
    if (tgt.d > 2.5) { u.state = ST_SEEK; steer(world, u, tgt.x, tgt.z, dt, speed); if (u.lod === LOD_FULL) separate(world, u, dt); }
    else { u.state = ST_IDLE; if (u.lod === LOD_FULL) separate(world, u, dt); }
    confine(world, u);
    return;
  }

  const range = atkDef.ranged ? u.atkRange : u.atkRange;
  if (tgt.d <= range * (atkDef.ranged ? 0.95 : 0.92)) {
    // in range → attack (or, for officers, sometimes raise guard first)
    if (u.atkTimer <= 0) {
      if ((u.type === UNIT_CAPTAIN || u.type === UNIT_WARLORD) && u.guardMax > 0 &&
          u.blockCd <= 0 && world.rng() < 0.25 && tgt.isPlayer) {
        u.state = ST_BLOCK; u.stateT = 0; u.blockCd = 2.5;
        u.heading = headingTo(u.x, u.z, tgt.x, tgt.z);
      } else {
        startEnemyAttack(world, u, tgt.x, tgt.z);
      }
    } else {
      // shuffle/face while on cooldown
      u.heading = angleApproach(u.heading, headingTo(u.x, u.z, tgt.x, tgt.z), 5 * dt);
      if (!atkDef.ranged && tgt.d > range * 0.6) steer(world, u, tgt.x, tgt.z, dt, speed * 0.5);
      u.state = ST_SEEK;
      if (u.lod === LOD_FULL) separate(world, u, dt);
    }
  } else {
    // approach
    u.state = ST_SEEK;
    steer(world, u, tgt.x, tgt.z, dt, speed);
    if (u.lod === LOD_FULL) separate(world, u, dt);
  }
  confine(world, u);
}

function updateBlockUnit(world, u, dt) {
  u.stateT += dt;
  const tgt = targetPos(world, u);
  if (tgt) u.heading = angleApproach(u.heading, headingTo(u.x, u.z, tgt.x, tgt.z), 4 * dt);
  if (u.stateT > 1.1 || !tgt) { u.state = ST_SEEK; u.stateT = 0; u.atkTimer = 0.1; }
}

// keep a unit on the field AND out of arena obstacles (walls/towers/spires/…)
function confine(world, u) {
  const lim = MAP_HALF - 2;
  if (u.x < -lim) u.x = -lim; else if (u.x > lim) u.x = lim;
  if (u.z < -lim) u.z = -lim; else if (u.z > lim) u.z = lim;
  world.colliders.resolve(u, 0.55 * u.scale);
}

export function updateCrowd(world, dt) {
  const units = world.units.active;
  // iterate backwards so despawns (swap-remove) don't skip anyone
  for (let i = units.length - 1; i >= 0; i--) {
    const u = units[i];
    // store previous position for render interpolation
    u.px = u.x; u.py = u.y; u.pz = u.z;
    if (u.state === ST_BLOCK) { updateBlockUnit(world, u, dt); confine(world, u); continue; }
    updateUnit(world, u, dt);
  }
  updateProjectiles(world, dt);
  updatePickups(world, dt);
}

// ---- archer ember bolts ----
function updateProjectiles(world, dt) {
  const arr = world.projectiles.active;
  for (let i = arr.length - 1; i >= 0; i--) {
    const b = arr[i];
    b.life -= dt;
    b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
    b.vy -= 4 * dt; // slight arc
    b.spin += dt * 10;
    let hit = false;
    // hit player?
    if (b.team === TEAM_MAGMAR) {
      const p = world.player;
      if (dist2(b.x, b.z, p.x, p.z) < 0.8 && Math.abs(b.y - (p.y + 1.1)) < 1.2) {
        damagePlayer(world, b.dmg, b.x, b.z, false);
        hit = true;
      }
    }
    // hit an opposing unit?
    if (!hit) {
      world.hash.someInRadius(b.x, b.z, 0.9, (slot) => {
        const o = world.units.get(slot);
        if (!o._active || o.team === b.team || o.state === ST_DEAD) return;
        if (Math.abs(b.y - 1.1) > 1.3) return;
        o.hp -= b.dmg; o.flash = 1;
        if (o.hp <= 0) world.killUnit(o, false);
        else applyReaction(world, o, RE_FLINCH, b.x, b.z, 2, 0);
        hit = true;
        return false; // stop
      });
    }
    if (hit || b.life <= 0 || b.y < 0) {
      for (let k = 0; k < 3; k++) {
        const a = world.rng() * 6.28;
        world.spawnSpark(b.x, b.y, b.z, Math.cos(a) * 3, world.rng() * 3, Math.sin(a) * 3, 0.25, 0.5, 0);
      }
      world.projectiles.despawn(b);
    }
  }
}

function updatePickups(world, dt) {
  const arr = world.pickups.active;
  const p = world.player;
  for (let i = arr.length - 1; i >= 0; i--) {
    const pk = arr[i];
    pk.life -= dt; pk.bob += dt * 3;
    if (dist2(pk.x, pk.z, p.x, p.z) < 2.2 * 2.2 && p.state !== 'dead') {
      if (pk.kind === 0) p.hp = Math.min(p.maxHp, p.hp + pk.value);
      world.spawnDmgNum(p.x, 2.6, p.z, pk.value, 4 /* heal */);
      world.pickups.despawn(pk);
      continue;
    }
    if (pk.life <= 0) world.pickups.despawn(pk);
  }
}
